import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';

const getBatch = (data) => {
  if (Array.isArray(data)) return data[0] || null;
  if (Array.isArray(data?.data)) return data.data[0] || null;
  return data || null;
};

const getRequestId = (row, fallback) => row?.requestId || row?.request_id || row?.id || fallback;
const getGroupLabel = (row) => row?.request?.xptPgnDesc || row?.request?.npaProductSum || '-';
const getBrandLabel = (row) => {
  if (!Array.isArray(row?.brand) || row.brand.length === 0) return 'UNMAPPED';
  if (row.brand.length === 1) return row.brand[0]?.brandName || '-';
  const matchedBrand = row.brand.find((item) => item?.isMatched);
  return matchedBrand?.brandName || 'MULTIPLE';
};
const isUnmappedOrMultipleBrand = (row) => {
  if (!Array.isArray(row?.brand) || row.brand.length === 0) return true;
  if (row.brand.length > 1) {
    return !row.brand.some((item) => item?.isMatched);
  }
  return false;
};
const buildSessionKey = () => Math.random().toString(36).slice(2, 8);

const ApproveNdc = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get('batch-id') || searchParams.get('batchId');
  const status = searchParams.get('status');
  const { loading, error, data, execute } = useAPICall();
  const [selectedIds, setSelectedIds] = useState([]);
  const [processingAction, setProcessingAction] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  useEffect(() => {
    const loadBatch = async () => {
      if (!batchId) return;
      try {
        await execute(API_CONFIG.workflow.getDrugBatchById, null, {
          params: { batchId },
          showErrorToast: false,
          errorMessage: 'Unable to load drug batch.',
        });
      } catch {
      }
    };

    loadBatch();
  }, [batchId, execute]);

  const batch = useMemo(() => getBatch(data), [data]);
  const rows = useMemo(() => (Array.isArray(batch?.batchRequests) ? batch.batchRequests : []), [batch]);
  const isUnmatched = status === 'un-matched';
  const groupHeader = useMemo(() => (
    rows[0]?.request?.source === 'NPA' ? 'Product Sum' : 'Group'
  ), [rows]);

  const toggleSelect = (requestId) => {
    setSelectedIds((prev) => (
      prev.includes(requestId) ? prev.filter((id) => id !== requestId) : [...prev, requestId]
    ));
  };

  const selectedRows = useMemo(
    () => rows.filter((row, index) => selectedIds.includes(getRequestId(row, index))),
    [rows, selectedIds],
  );

  const persistSelectionAndNavigate = (path, items) => {
    const key = buildSessionKey();
    const payload = {
      dcr: [{ ...batch, batchRequests: items }],
      batchReq: rows,
      selected: items,
      reqId: items.map((item, index) => getRequestId(item, index)),
      src: batch?.batchRequests?.[0]?.request?.source || '',
    };
    sessionStorage.setItem(key, JSON.stringify(payload));
    navigate(`${path}?key=${key}`);
  };

  const closeSelected = async () => {
    if (selectedRows.length === 0) {
      toast.error('Please select at least one NDC to close.');
      return;
    }

    const invalidSelection = selectedRows.some((row) => {
      const rowStatus = String(row.status || '').toUpperCase();
      return rowStatus === 'DUPLICATE' || rowStatus === 'OPEN';
    });

    if (invalidSelection) {
      toast.error('Please select only those NDCs with a MAPPED status.');
      return;
    }

    const batchStatusArray = rows.map((row) => String(row.status || '').toUpperCase());
    if (batchStatusArray.includes('DUPLICATE') || batchStatusArray.includes('OPEN')) {
      toast.error('DCR has NDCs with OPEN/DUPLICATE status.');
      return;
    }

    try {
      setProcessingAction('close');
      await execute(API_CONFIG.workflow.closeDrugDcrBatch, {
        data: selectedRows.map((row, index) => getRequestId(row, index)),
      }, {
        params: { batchId },
        successMessage: 'Selected NDCs closed successfully.',
        showSuccessToast: true,
        errorMessage: 'Unable to close selected NDCs.',
      });
      setSelectedIds([]);
      if (selectedRows.length === rows.length) {
        navigate('/inbox/drug');
        return;
      }
      await execute(API_CONFIG.workflow.getDrugBatchById, null, {
        params: { batchId },
        showErrorToast: false,
        errorMessage: 'Unable to refresh drug batch.',
      });
    } catch {
    } finally {
      setProcessingAction('');
    }
  };

  const resolveDuplicates = () => {
    const duplicateRows = selectedRows.length > 0
      ? selectedRows
      : rows.filter((row) => String(row.status || '').toUpperCase() === 'DUPLICATE');

    if (duplicateRows.length === 0) {
      toast.error('There are no duplicates.');
      return;
    }

    const hasNonDuplicate = duplicateRows.some((row) => String(row.status || '').toUpperCase() !== 'DUPLICATE');
    if (hasNonDuplicate) {
      toast.error('Please select only those NDCs with a duplicate status.');
      return;
    }

    persistSelectionAndNavigate('/drug-matching', duplicateRows);
  };

  const mapBrandPage = () => {
    if (selectedRows.length === 0) {
      toast.error('Please select NDCs for Brand Mapping.');
      return;
    }

    const invalidRows = selectedRows.some((row) => !isUnmappedOrMultipleBrand(row));
    if (invalidRows) {
      toast.error('Please select only those NDCs with a UNMAPPED OR MULTIPLE brand.');
      return;
    }

    const firstGroup = getGroupLabel(selectedRows[0]);
    const sameGroup = selectedRows.every((row) => getGroupLabel(row) === firstGroup);
    if (!sameGroup) {
      toast.error(
        rows[0]?.request?.source === 'NPA'
          ? 'Please select NDCs that belong to the same Product Sum.'
          : 'Please select NDCs that belong to the same Group.',
      );
      return;
    }

    persistSelectionAndNavigate('/drug-brand-map', selectedRows);
  };

  const pageHeaderSx = {
    px: 2,
    py: 1.5,
    borderBottom: '1px solid #E5E7EB',
    borderLeft: '4px solid #1E88E5',
    background: "#fbfdfe",
    gap: 2,
    mb: 3,
  };

  return (
    <Box sx={{ maxWidth: '100vw', pb: 6, mt: 1, px: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <PageBreadcrumbs items={[
          { label: 'Drug Inbox', path: '/inbox/drug' },
          { label: 'Approve NDC' }
        ]} />
      </Box>

      <Paper elevation={0} sx={{
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        backgroundColor: '#fff',
        overflow: 'hidden'
      }}>
        <Box sx={pageHeaderSx}>
          <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:"space-between", gap: 1.5 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 0.25, lineHeight: 1.2 }}>
                Drug Batch Requests
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                Review NDC requests in the selected drug inbox batch.
              </Typography>
            </Box>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button className="action-btn close-btn"
                  variant="outlined"
                  onClick={resolveDuplicates}
                  disabled={!isUnmatched || loading || processingAction !== ''}
                >
                  Resolve Duplicates
                </Button>
                <Button className="action-btn close-btn"
                  variant="outlined"
                  onClick={mapBrandPage}
                  disabled={!isUnmatched || loading || processingAction !== ''}
                >
                  Map Brand
                </Button>
                <Button className="action-btn close-btn"
                  variant="contained"
                  onClick={closeSelected}
                  disabled={selectedRows.length === 0 || processingAction === 'close'}
                >
                  {processingAction === 'close' ? 'Closing...' : 'Close'}
                </Button>
              </Box>
          </Box>
        </Box>

        <Box sx={{ px: 0, pb: 0 }}>
          {loading ? (
            <Grid container spacing={2}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Grid item xs={12} md={3} key={i}>
                  <Skeleton variant="text" width="80%" height={24} />
                </Grid>
              ))}
            </Grid>
          ) : (
              <>
           <Grid container spacing={2} sx={{ px: 2.5 ,pb:2, borderBottom:"1px solid #eaeaea"}}>
              <Grid item xs={12} md={3}>
                <Typography variant="body2"><strong>Batch ID:</strong> {batch?.batchId || batchId || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2"><strong>Status:</strong> {batch?.batchStatus || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2"><strong>Created:</strong> {batch?.batchCreatedTime || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2"><strong>Modified:</strong> {batch?.batchLastUpdatedTime || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2"><strong>NDC Count:</strong> {rows.length}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2"><strong>Source:</strong> {rows[0]?.request?.source || '-'}</Typography>
              </Grid>
            </Grid>

          <Paper sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderRadius: 2, overflow: 'hidden' }}>
            <GlobalMaterialTable
              tableTitle="Batch Requests"
              tableCount={rows.length}
              columns={[
                {
                  header: 'Select',
                  id: 'select',
                  size: 40,
                  minSize: 40,
                  maxSize: 40,
                  enableSorting: false,
                  Cell: ({ row }) => {
                    const requestId = getRequestId(row.original, row.index);
                    const isClosed = String(row.original.status || '').toLowerCase() === 'closed';
                    return (
                      <Checkbox
                        checked={selectedIds.includes(requestId)}
                        onChange={() => toggleSelect(requestId)}
                        disabled={isClosed}
                        sx={{
                        p: 0.5,
                        '& .MuiSvgIcon-root': {
                          fontSize: 16,
                        },
                      }}
                      />
                    );
                  },
                },
                { header: 'NDC', id: 'ndc', accessorFn: (row) => row.request?.ndc || row.ndc || '-' },
                { header: 'Source', id: 'source', accessorFn: (row) => row.request?.source || row.source || '-' },
                { header: groupHeader, id: 'group', accessorFn: (row) => getGroupLabel(row) },
                { header: 'Brand', id: 'brand', accessorFn: (row) => getBrandLabel(row) },
                { header: 'Date', id: 'date', accessorFn: (row) => row.lastUpdatedTime || row.modifiedTime || '-' },
                { header: 'Status', id: 'status', accessorFn: (row) => row.status || '-' },
              ]}
              data={rows}
              emptyMessage="No requests found for this batch"
              enablePagination
              enableBottomToolbar
              OuterBorder={"none"}
              manualPagination={false}
              page={pagination.pageIndex + 1}
              pageSize={pagination.pageSize}
              onPageChange={(p) => setPagination(prev => ({ ...prev, pageIndex: p - 1 }))}
              onPageSizeChange={(ps) => setPagination(prev => ({ ...prev, pageSize: ps }))}
            />
          </Paper>
        </>
      )
    }
    </Box>
</Paper>
    </Box>
  );
};

export default ApproveNdc;
